/**
 * Sprint 23.8 — MetricScorecard (shared scorecard primitive).
 *
 * A titled grid of `<MetricCard>`s. Generalised from the Sprint 23.6 v2
 * AgentScorecard so the agent profile ("this week" for one hire) and the
 * workspace home ("this week" across the whole workforce) share one component
 * instead of cloning the grid + delta CSS. `columns` defaults to 4 (the locked
 * 4-up layout); `style` lets a caller add a section frame (e.g. the agent
 * profile's hairline bottom-border between hero sections) without re-specifying
 * the header or grid.
 */

import { type CSSProperties } from "react";
import { MetricCard, type MetricCardProps } from "./MetricCard";

export interface MetricScorecardProps {
  title: string;
  metrics: MetricCardProps[];
  /** Grid columns. Default 4. */
  columns?: number;
  /** Merged into the <section> frame (padding / border between sections). */
  style?: CSSProperties;
}

export function MetricScorecard({ title, metrics, columns = 4, style }: MetricScorecardProps) {
  return (
    <section style={{ ...wrap, ...style }}>
      <h3 style={header}>{title}</h3>
      <div style={{ ...grid, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {metrics.map((m) => (
          <MetricCard key={m.label} {...m} />
        ))}
      </div>
    </section>
  );
}

const wrap: CSSProperties = {
  // No frame by default — callers add padding/border via `style`.
};

const header: CSSProperties = {
  margin: "0 0 6px",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 8,
};
